import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Trash2, Loader2, Send, Save, Star, PoundSterling, Search, Pencil } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { PROPERTY_TYPES, WINDOW_TYPES, INITIAL_SURVEY_FORM, INITIAL_OPENING, MATERIALS, LOUVRE_SIZES, STILES_OPTIONS } from './constants';
import OpeningForm from './OpeningForm';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateSurveyDialog = ({ open, onOpenChange, onCreated, createSurvey, initialCustomerId, initialCustomerData }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [formData, setFormData] = useState(INITIAL_SURVEY_FORM);
  const [currentOpening, setCurrentOpening] = useState(null);
  const [editingOpeningIndex, setEditingOpeningIndex] = useState(null);
  const [customTotalPrice, setCustomTotalPrice] = useState(null);
  // Per-material retail overrides: { paulownia: 500, basswood: null, ... }
  const [materialOverrides, setMaterialOverrides] = useState({});
  
  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Load customer data if initialCustomerId is provided
  useEffect(() => {
    if (initialCustomerId && open) {
      loadCustomer(initialCustomerId);
    }
  }, [initialCustomerId, open]);

  // Pre-fill customer data if initialCustomerData is provided (from appointment)
  useEffect(() => {
    if (initialCustomerData && open) {
      selectCustomer(initialCustomerData);
    }
  }, [initialCustomerData, open]);

  // Search customers
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch.length >= 2) {
        searchCustomers(customerSearch);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  const loadCustomer = async (customerId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        selectCustomer(response.data);
      }
    } catch (error) {
      console.error('Error loading customer:', error);
    }
  };

  const searchCustomers = async (query) => {
    setIsSearching(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/customers?search=${encodeURIComponent(query)}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.customers || []);
    } catch (error) {
      console.error('Error searching customers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.id,
      customer_name: customer.name || customer.full_name || '',
      customer_email: customer.email || '',
      customer_phone: customer.phone || '',
      customer_address: customer.address || customer.address1 || '',
      customer_postcode: customer.postcode || customer.postal_code || ''
    }));
    setCustomerSearch('');
    setSearchResults([]);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({
      ...prev,
      customer_id: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      customer_address: '',
      customer_postcode: ''
    }));
  };

  // Calculate pricing for ALL materials — retail, cost, margin per material
  const pricingData = useMemo(() => {
    let totalSqm = 0;
    const vatRate = formData.vat_rate ?? 20;
    const vatMultiplier = vatRate === 0 ? (1 / 1.20) : 1;
    const selectedMats = formData.selected_quote_materials || ['paulownia'];

    // Calculate sqm per opening
    const openingSqms = formData.openings.map(opening => {
      const width = parseFloat(opening.width) || 0;
      const height = parseFloat(opening.height) || 0;
      const sqm = (width * height) / 1000000;
      totalSqm += sqm;
      return { ...opening, sqm, label: opening.room_location || 'Opening', layout_code: opening.layout || 'N/A' };
    });

    // Build pricing for each material
    const allMaterials = {};
    MATERIALS.forEach(mat => {
      const baseRetail = openingSqms.reduce((sum, o) => sum + Math.round(o.sqm * mat.pricePerSqm * 100) / 100, 0);
      const retailWithVat = Math.round(baseRetail * vatMultiplier * 100) / 100;
      const overrideVal = materialOverrides[mat.id];
      const hasOverride = overrideVal !== undefined && overrideVal !== null && overrideVal !== '';
      const retailTotal = hasOverride ? parseFloat(overrideVal) : retailWithVat;
      const rrp = retailWithVat;
      const discount = Math.round((rrp - retailTotal) * 100) / 100;

      // Cost calculation (surveyor only)
      let costTotal = 0;
      if (mat.costPerSqm !== null) {
        costTotal = Math.round(openingSqms.reduce((sum, o) => sum + o.sqm * mat.costPerSqm, 0) * 100) / 100;
      }

      const upfrontRatio = mat.upfrontPricePerSqm / mat.pricePerSqm;
      const upfrontTotal = Math.round(retailTotal * upfrontRatio * 100) / 100;
      const margin = mat.costPerSqm !== null ? Math.round((retailTotal - costTotal) * 100) / 100 : null;
      const marginPct = mat.costPerSqm !== null && retailTotal > 0 ? Math.round((margin / retailTotal) * 100) : null;

      // Per-window prices (proportionally scaled if overridden)
      const ratio = retailWithVat > 0 ? retailTotal / retailWithVat : 1;
      const windowPrices = openingSqms.map(o => ({
        label: o.label,
        sqm: o.sqm,
        standard: Math.round(o.sqm * mat.pricePerSqm * vatMultiplier * ratio * 100) / 100,
        upfront: Math.round(o.sqm * mat.upfrontPricePerSqm * vatMultiplier * ratio * 100) / 100,
      }));

      const effectiveSqm = hasOverride && totalSqm > 0 ? Math.round(retailTotal / totalSqm * 100) / 100 : null;

      allMaterials[mat.id] = {
        id: mat.id,
        name: mat.name,
        leadTime: mat.leadTime,
        retailTotal: Math.round(retailTotal * 100) / 100,
        rrp: Math.round(rrp * 100) / 100,
        discount: discount > 0.01 ? discount : 0,
        hasDiscount: discount > 0.01,
        upfrontTotal: Math.round(upfrontTotal * 100) / 100,
        upfrontSaving: Math.round((retailTotal - upfrontTotal) * 100) / 100,
        deposit: Math.round(retailTotal * 0.6 * 100) / 100,
        balance: Math.round(retailTotal * 0.4 * 100) / 100,
        costTotal,
        margin,
        marginPct,
        hasCost: mat.costPerSqm !== null,
        pricePerSqm: mat.pricePerSqm,
        costPerSqm: mat.costPerSqm,
        hasOverride,
        effectiveSqm,
        windowPrices,
        isSelected: selectedMats.includes(mat.id),
      };
    });

    return {
      totalSqm: totalSqm.toFixed(2),
      totalSqmNum: totalSqm,
      vatRate,
      materials: allMaterials,
      selectedMaterials: selectedMats,
      openingSqms,
    };
  }, [formData.openings, formData.vat_rate, formData.selected_quote_materials, materialOverrides]);

  // Reset custom price when openings change
  const handleOpeningsChange = () => {
    setCustomTotalPrice(null);
  };

  const resetForm = () => {
    setStep(1);
    setFormData(INITIAL_SURVEY_FORM);
    setCurrentOpening(null);
    setEditingOpeningIndex(null);
    setCustomTotalPrice(null);
    setMaterialOverrides({});
    setCustomerSearch('');
    setSearchResults([]);
    setSelectedCustomer(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await createSurvey(formData);
      if (response.success) {
        toast({
          title: "Survey Created",
          description: `Survey ${response.data.survey.survey_number} created successfully`,
        });
        resetForm();
        onOpenChange(false);
        onCreated();
      } else {
        throw new Error(response.data?.error || 'Failed to create survey');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create survey",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAndSendQuote = async () => {
    if (!formData.customer_email) {
      toast({
        title: "Email Required",
        description: "Customer email is required to send the quote",
        variant: "destructive",
      });
      return;
    }

    if (formData.openings.length === 0) {
      toast({
        title: "Openings Required",
        description: "Please add at least one opening to create a quote",
        variant: "destructive",
      });
      return;
    }

    setSendingQuote(true);
    try {
      // First create the survey
      const surveyResponse = await createSurvey(formData);
      if (!surveyResponse.success) {
        throw new Error(surveyResponse.data?.error || 'Failed to create survey');
      }

      // Build quote payload — only selected materials, NO cost prices
      const selectedMats = formData.selected_quote_materials || ['paulownia'];
      const primaryMat = pricingData.materials[selectedMats[0]] || pricingData.materials['paulownia'];
      
      // Build per-window prices for each selected material
      const windowsPayload = pricingData.openingSqms.map((o, i) => {
        const w = { label: o.label || 'Window', layout_code: o.layout_code || 'N/A', width: parseFloat(o.width) || 0, height: parseFloat(o.height) || 0, sqm: o.sqm, price: primaryMat.windowPrices[i]?.standard || 0 };
        selectedMats.forEach(matId => {
          const mp = pricingData.materials[matId];
          if (mp) {
            w[`${matId}_standard`] = mp.windowPrices[i]?.standard || 0;
            w[`${matId}_upfront`] = mp.windowPrices[i]?.upfront || 0;
          }
        });
        return w;
      });

      // Build pricing_options for selected materials only (no cost data)
      const pricingOptions = {};
      selectedMats.forEach(matId => {
        const mp = pricingData.materials[matId];
        if (mp) {
          pricingOptions[matId] = {
            standard_total: mp.retailTotal,
            rrp_total: mp.rrp,
            upfront_total: mp.upfrontTotal,
            deposit: mp.deposit,
            balance: mp.balance,
            price_per_sqm: mp.pricePerSqm,
          };
        }
      });

      const quotePayload = {
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone || '',
        customer_address: formData.customer_address || '',
        material: primaryMat.name,
        louvre_size: formData.louvre_size || '76mm',
        colour: formData.colour || 'White',
        control_type: formData.control_type || 'hidden_tilt_rod',
        stiles: formData.stiles || 'beaded',
        hinge_colour: formData.hinge_colour || 'White',
        vat_rate: formData.vat_rate ?? 20,
        windows: windowsPayload,
        total_price: primaryMat.retailTotal + blindsTotal,
        customer_notes: formData.customer_notes || null,
        blinds: formData.include_blinds && formData.blinds.length > 0 
          ? formData.blinds.filter(b => b.room_name || b.description).map(b => ({
              room_name: b.room_name || 'Blind',
              description: b.description || '',
              price: parseFloat(b.price) || 0
            }))
          : [],
        pricing_options: pricingOptions,
      };
      
      console.log('Quote Payload:', JSON.stringify(quotePayload, null, 2));

      const quoteResponse = await fetch(`${API_URL}/api/quotes/send-formal-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quotePayload)
      });

      const quoteResult = await quoteResponse.json();

      if (quoteResult.success) {
        toast({
          title: "Survey Created & Quote Sent!",
          description: `Quote sent to ${formData.customer_email}. Nurture campaign started.`,
        });
        resetForm();
        onOpenChange(false);
        onCreated();
      } else {
        throw new Error(quoteResult.detail || 'Failed to send quote');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create survey and send quote",
        variant: "destructive",
      });
    } finally {
      setSendingQuote(false);
    }
  };

  const addOpening = (opening) => {
    if (editingOpeningIndex !== null) {
      // Update existing opening
      setFormData(prev => ({
        ...prev,
        openings: prev.openings.map((o, i) => i === editingOpeningIndex ? opening : o)
      }));
      setEditingOpeningIndex(null);
    } else {
      // Add new opening
      setFormData(prev => ({
        ...prev,
        openings: [...prev.openings, opening]
      }));
    }
    setCurrentOpening(null);
  };

  const startEditOpening = (index) => {
    setEditingOpeningIndex(index);
    setCurrentOpening({ ...formData.openings[index] });
  };

  // Blinds helpers
  const addBlind = () => {
    setFormData(prev => ({
      ...prev,
      blinds: [...prev.blinds, { room_name: '', description: '', price: '' }]
    }));
  };

  const updateBlind = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      blinds: prev.blinds.map((b, i) => i === index ? { ...b, [field]: value } : b)
    }));
  };

  const removeBlind = (index) => {
    setFormData(prev => ({
      ...prev,
      blinds: prev.blinds.filter((_, i) => i !== index)
    }));
  };

  const blindsTotal = useMemo(() => {
    return formData.blinds.reduce((sum, b) => sum + (parseFloat(b.price) || 0), 0);
  }, [formData.blinds]);

  const removeOpening = (index) => {
    setFormData(prev => ({
      ...prev,
      openings: prev.openings.filter((_, i) => i !== index)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) resetForm(); onOpenChange(isOpen); }}>
      <DialogTrigger asChild>
        <Button className="bg-[#8CB89F] hover:bg-[#7aa88f]" data-testid="create-survey-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Survey
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Survey</DialogTitle>
          <DialogDescription>
            Fill in the customer and property details, then add openings.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={`step-${step}`} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="step-1" onClick={() => setStep(1)}>
              1. Customer Info
            </TabsTrigger>
            <TabsTrigger value="step-2" onClick={() => setStep(2)}>
              2. Openings
            </TabsTrigger>
            <TabsTrigger value="step-3" onClick={() => setStep(3)}>
              3. Notes & Review
            </TabsTrigger>
          </TabsList>

          {/* Step 1: Customer Info */}
          <TabsContent value="step-1" className="space-y-4 mt-4">
            {/* Customer Search */}
            {!selectedCustomer && (
              <div className="space-y-2">
                <Label>Search Existing Customer</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by name, email, or phone..."
                    className="pl-10"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
                
                {searchResults.length > 0 && (
                  <div className="border rounded-lg shadow-lg max-h-40 overflow-y-auto bg-white">
                    {searchResults.map((customer) => (
                      <div
                        key={customer.id}
                        onClick={() => selectCustomer(customer)}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <div className="font-medium">{customer.name || customer.full_name}</div>
                        <div className="text-sm text-gray-500">
                          {customer.email} {customer.phone && `• ${customer.phone}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Customer Banner */}
            {selectedCustomer && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-green-800">Customer: {selectedCustomer.name || selectedCustomer.full_name}</div>
                    <div className="text-sm text-green-600">
                      {selectedCustomer.email} {selectedCustomer.phone && `• ${selectedCustomer.phone}`}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearCustomer}>
                    Clear
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer_name">Customer Name *</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Smith"
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_email">Email</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_phone">Phone</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                  placeholder="07123456789"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_postcode">Postcode</Label>
                <Input
                  id="customer_postcode"
                  value={formData.customer_postcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_postcode: e.target.value }))}
                  placeholder="SW1A 1AA"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="customer_address">Address</Label>
                <Input
                  id="customer_address"
                  value={formData.customer_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                  placeholder="123 Example Street, London"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="survey_date">Survey Date</Label>
                <Input
                  id="survey_date"
                  type="datetime-local"
                  value={formData.survey_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, survey_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surveyor_name">Surveyor Name</Label>
                <Input
                  id="surveyor_name"
                  value={formData.surveyor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, surveyor_name: e.target.value }))}
                  placeholder="Staff member name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type</Label>
                <Select 
                  value={formData.property_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, property_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_notes">Property Notes</Label>
                <Input
                  id="property_notes"
                  value={formData.property_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, property_notes: e.target.value }))}
                  placeholder="Any relevant property details"
                />
              </div>
            </div>

            {/* VAT Rate Selection */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">VAT Rate</Label>
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => setFormData(prev => ({ ...prev, vat_rate: 20 }))}
                  className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                    formData.vat_rate === 20
                      ? 'border-[#8CB89F] bg-[#8CB89F]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="vat-rate-20"
                >
                  <p className="font-medium">Standard Rate — 20%</p>
                  <p className="text-xs text-gray-500">All prices include VAT</p>
                </div>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, vat_rate: 0 }))}
                  className={`cursor-pointer rounded-lg border-2 p-3 transition-all ${
                    formData.vat_rate === 0
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="vat-rate-0"
                >
                  <p className="font-medium">Zero Rated — 0%</p>
                  <p className="text-xs text-gray-500">Qualifying new builds</p>
                </div>
              </div>
              {formData.vat_rate === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  VAT will be zero-rated. Prices will be reduced by the 20% VAT element for this qualifying new build.
                </p>
              )}
            </div>

            {/* Material Selection */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Material Selection</Label>
              <div className="grid grid-cols-1 gap-2">
                {MATERIALS.map(material => (
                  <div
                    key={material.id}
                    onClick={() => setFormData(prev => ({ ...prev, material: material.id }))}
                    className={`cursor-pointer rounded-lg border-2 p-3 transition-all relative ${
                      formData.material === material.id 
                        ? 'border-[#8CB89F] bg-[#8CB89F]/5' 
                        : material.recommended 
                          ? 'border-amber-400 hover:border-amber-500 bg-amber-50/50'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`material-${material.id}`}
                  >
                    {material.recommended && (
                      <div className="absolute -top-2 left-3 px-2 py-0.5 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3 fill-white" />
                        Recommended
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{material.name}</p>
                        <p className="text-sm text-gray-500">{material.description}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-[#2F4F4F]">£{material.pricePerSqm}</span>
                        <span className="text-xs text-gray-500 block">/m² inc VAT</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hinge Colour */}
            <div className="space-y-2">
              <Label htmlFor="hinge_colour">Hinge Colour</Label>
              <Input
                id="hinge_colour"
                value={formData.hinge_colour}
                onChange={(e) => setFormData(prev => ({ ...prev, hinge_colour: e.target.value }))}
                placeholder="White"
              />
            </div>

            {/* Survey-Level Shutter Defaults */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Shutter Specifications (Default for all openings)</Label>
              <p className="text-sm text-gray-500">These will be applied to all openings. You can override per-window if needed.</p>
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="louvre_size">Louvre Size</Label>
                  <Select 
                    value={formData.louvre_size} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, louvre_size: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LOUVRE_SIZES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="colour">Colour</Label>
                  <Input
                    id="colour"
                    value={formData.colour}
                    onChange={(e) => setFormData(prev => ({ ...prev, colour: e.target.value }))}
                    placeholder="White"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="control_type">Control Type</Label>
                  <Select 
                    value={formData.control_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, control_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="central_tilt_rod">Central Tilt Rod</SelectItem>
                      <SelectItem value="hidden_tilt_rod">Hidden Tilt Rod</SelectItem>
                      <SelectItem value="remote">Remote Control</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stiles">Stiles</Label>
                  <Select 
                    value={formData.stiles} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, stiles: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STILES_OPTIONS.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!formData.customer_name}>
                Next: Add Openings
              </Button>
            </div>
          </TabsContent>

          {/* Step 2: Openings */}
          <TabsContent value="step-2" className="space-y-4 mt-4">
            {/* Live Pricing Banner — shows Paulownia+ as default */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-r from-[#2F4F4F] to-[#3d6363] text-white p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-white/50 uppercase tracking-wider">{pricingData.totalSqm} m² total</p>
                  {pricingData.vatRate === 0 && (
                    <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">VAT Zero Rated</span>
                  )}
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-sm text-white/70">Paulownia+ (default)</span>
                  <span className="text-2xl font-bold">£{(pricingData.materials.paulownia?.retailTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                </div>
                <p className="text-xs text-white/40 mt-1">All material options available on review screen</p>
              </div>
            )}

            {/* Added Openings List */}
            {formData.openings.length > 0 && (
              <div className="space-y-2">
                <Label>Added Openings ({formData.openings.length})</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.openings.map((opening, index) => {
                    const sqm = ((parseFloat(opening.width) || 0) * (parseFloat(opening.height) || 0)) / 1000000;
                    const extraMeasurements = [];
                    if (opening.midrail) extraMeasurements.push(`Midrail: ${opening.midrail}mm`);
                    if (opening.split_height) extraMeasurements.push(`Split: ${opening.split_height}mm`);
                    
                    return (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded border">
                        <div className="flex-1">
                          <span className="font-medium">{opening.room_location}</span>
                          <span className="text-sm text-gray-500 ml-2">
                            ({WINDOW_TYPES.find(l => l.value === opening.window_type)?.label})
                          </span>
                          <div className="text-xs text-gray-500 mt-1">
                            {opening.width}mm × {opening.height}mm = {sqm.toFixed(2)} m²
                            {opening.layout && <span className="ml-2 font-mono bg-gray-200 px-1 rounded">{opening.layout}</span>}
                          </div>
                          {extraMeasurements.length > 0 && (
                            <div className="text-xs text-[#8CB89F] mt-1">{extraMeasurements.join(' | ')}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">{sqm.toFixed(2)} m²</span>
                          <Button variant="ghost" size="sm" onClick={() => startEditOpening(index)} data-testid={`edit-opening-${index}`}>
                            <Pencil className="h-4 w-4 text-[#2F4F4F]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeOpening(index)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add/Edit Opening Form */}
            {currentOpening === null ? (
              <Button 
                variant="outline" 
                onClick={() => {
                  setEditingOpeningIndex(null);
                  setCurrentOpening({
                    ...INITIAL_OPENING,
                    material: formData.material,
                    louvre_size: formData.louvre_size,
                    colour: formData.colour,
                    control_type: formData.control_type,
                    stiles: formData.stiles,
                  });
                }} 
                className="w-full" 
                data-testid="add-opening-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Opening
              </Button>
            ) : (
              <>
                {editingOpeningIndex !== null && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                    <Pencil className="h-3.5 w-3.5" />
                    Editing: {formData.openings[editingOpeningIndex]?.room_location || `Opening ${editingOpeningIndex + 1}`}
                  </div>
                )}
                <OpeningForm 
                  opening={currentOpening}
                  onChange={setCurrentOpening}
                  onSave={addOpening}
                  onCancel={() => { setCurrentOpening(null); setEditingOpeningIndex(null); }}
                  isEditing={editingOpeningIndex !== null}
                />
              </>
            )}

            {/* Blinds Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  id="include_blinds"
                  checked={formData.include_blinds}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_blinds: e.target.checked, blinds: e.target.checked ? prev.blinds : [] }))}
                  className="w-4 h-4 rounded border-stone-300"
                  data-testid="include-blinds-checkbox"
                />
                <Label htmlFor="include_blinds" className="text-sm font-medium cursor-pointer">Include Blinds in this survey</Label>
              </div>

              {formData.include_blinds && (
                <div className="space-y-3 pl-1">
                  {formData.blinds.map((blind, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_2fr_auto_auto] gap-2 items-end" data-testid={`blind-row-${idx}`}>
                      <div>
                        {idx === 0 && <Label className="text-xs text-stone-500">Room Name</Label>}
                        <Input
                          placeholder="e.g. Kitchen"
                          value={blind.room_name}
                          onChange={(e) => updateBlind(idx, 'room_name', e.target.value)}
                        />
                      </div>
                      <div>
                        {idx === 0 && <Label className="text-xs text-stone-500">Description</Label>}
                        <Input
                          placeholder="e.g. Roller blind, white, 120cm"
                          value={blind.description}
                          onChange={(e) => updateBlind(idx, 'description', e.target.value)}
                        />
                      </div>
                      <div>
                        {idx === 0 && <Label className="text-xs text-stone-500">Price (£)</Label>}
                        <Input
                          type="number"
                          placeholder="0.00"
                          value={blind.price}
                          onChange={(e) => updateBlind(idx, 'price', e.target.value)}
                          className="w-24"
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeBlind(idx)} className="text-red-500 hover:text-red-700 h-9 w-9 p-0">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addBlind} data-testid="add-blind-btn">
                    <Plus className="h-4 w-4 mr-1" /> Add Blind
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={() => setStep(3)}>Next: Notes & Review</Button>
            </div>
          </TabsContent>

          {/* Step 3: Notes & Review */}
          <TabsContent value="step-3" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer_notes">Customer Notes (included in quote email)</Label>
                <Textarea
                  id="customer_notes"
                  value={formData.customer_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_notes: e.target.value }))}
                  placeholder="Notes to include in the customer's quote email..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="general_notes">Internal Notes (staff only)</Label>
                <Textarea
                  id="general_notes"
                  value={formData.general_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, general_notes: e.target.value }))}
                  placeholder="General observations about the property..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="access_notes">Access Notes</Label>
                <Textarea
                  id="access_notes"
                  value={formData.access_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, access_notes: e.target.value }))}
                  placeholder="Information about property access for installation..."
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="installation_notes">Installation Notes</Label>
                <Textarea
                  id="installation_notes"
                  value={formData.installation_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, installation_notes: e.target.value }))}
                  placeholder="Special installation requirements..."
                  rows={2}
                />
              </div>
            </div>

            {/* Material Selection & Pricing — Surveyor View */}
            {formData.openings.length > 0 && (
              <div className="bg-gradient-to-br from-[#2F4F4F] to-[#3d6363] text-white p-5 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <PoundSterling className="w-5 h-5" />
                    <h4 className="font-semibold text-lg">Pricing & Material Selection</h4>
                  </div>
                  <span className="text-xs text-white/50">{pricingData.totalSqm} m² total</span>
                </div>

                {/* VAT Toggle */}
                <div className="flex items-center gap-3 mb-4 bg-white/10 rounded-lg p-3">
                  <span className="text-sm text-white/70">VAT:</span>
                  <button onClick={() => setFormData(prev => ({ ...prev, vat_rate: 20 }))}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${formData.vat_rate === 20 ? 'bg-white text-[#2F4F4F]' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                    data-testid="summary-vat-20">Standard 20%</button>
                  <button onClick={() => setFormData(prev => ({ ...prev, vat_rate: 0 }))}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${formData.vat_rate === 0 ? 'bg-amber-500 text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'}`}
                    data-testid="summary-vat-0">Zero Rated (New Build)</button>
                </div>

                <p className="text-xs text-white/50 mb-3 uppercase tracking-wider">Select materials to show customer — tick to include in quote</p>

                {/* Material rows */}
                <div className="space-y-2">
                  {MATERIALS.map(mat => {
                    const mp = pricingData.materials[mat.id];
                    if (!mp) return null;
                    const isSelected = (formData.selected_quote_materials || []).includes(mat.id);
                    
                    const toggleMaterial = () => {
                      setFormData(prev => {
                        const current = prev.selected_quote_materials || ['paulownia'];
                        if (current.includes(mat.id)) {
                          if (current.length <= 1) return prev;
                          return { ...prev, selected_quote_materials: current.filter(id => id !== mat.id) };
                        }
                        return { ...prev, selected_quote_materials: [...current, mat.id] };
                      });
                    };

                    return (
                      <div key={mat.id} className={`rounded-lg p-3 transition-all ${isSelected ? 'bg-white/15 border border-white/30' : 'bg-white/5 border border-transparent'}`}>
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={isSelected} onChange={toggleMaterial}
                            className="w-4 h-4 rounded" data-testid={`select-material-${mat.id}`} />
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm">{mat.name}</span>
                              <span className="text-[10px] text-white/40">{mat.leadTime}</span>
                              {mat.id === 'paulownia' && <span className="text-[10px] bg-[#8CB89F] text-white px-1.5 py-0.5 rounded">Default</span>}
                            </div>
                          </div>

                          <div className="text-right">
                            <span className="text-[10px] text-white/40 block">Retail</span>
                            <div className="flex items-center gap-1">
                              <span className="text-white/50 text-xs">£</span>
                              <Input type="number" step="0.01"
                                value={materialOverrides[mat.id] !== undefined ? materialOverrides[mat.id] : mp.rrp}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setMaterialOverrides(prev => ({ ...prev, [mat.id]: v === '' ? undefined : v }));
                                }}
                                className="w-24 bg-white/10 border-white/30 text-white text-sm font-bold text-right h-7 px-1.5"
                                data-testid={`retail-${mat.id}`} />
                            </div>
                            {mp.hasOverride && mp.effectiveSqm && (
                              <p className="text-[10px] text-amber-300">£{mp.effectiveSqm}/m²
                                <button onClick={() => setMaterialOverrides(prev => { const n = {...prev}; delete n[mat.id]; return n; })} className="text-white/40 hover:text-white ml-1 underline">reset</button>
                              </p>
                            )}
                          </div>

                          <div className="text-right w-20">
                            <span className="text-[10px] text-white/40 block">Cost</span>
                            {mp.hasCost ? (
                              <span className="text-sm text-red-300/80 font-medium">£{mp.costTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                            ) : (
                              <span className="text-xs text-white/30">—</span>
                            )}
                          </div>

                          <div className="text-right w-20">
                            <span className="text-[10px] text-white/40 block">Margin</span>
                            {mp.margin !== null ? (
                              <div>
                                <span className={`text-sm font-medium ${mp.margin > 0 ? 'text-green-300' : 'text-red-300'}`}>
                                  £{mp.margin.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                                </span>
                                <span className="text-[10px] text-white/40 ml-1">{mp.marginPct}%</span>
                              </div>
                            ) : (
                              <span className="text-xs text-white/30">—</span>
                            )}
                          </div>
                        </div>

                        {isSelected && (
                          <div className="mt-2 pt-2 border-t border-white/10 grid grid-cols-3 gap-2 text-xs text-white/60">
                            <div>Deposit (60%): <span className="text-white/80 font-medium">£{mp.deposit.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                            <div>Balance (40%): <span className="text-white/80 font-medium">£{mp.balance.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                            <div>Upfront (10% off): <span className="text-[#8CB89F] font-medium">£{mp.upfrontTotal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-3 text-xs text-white/40">
                  {(formData.selected_quote_materials || []).length} material{(formData.selected_quote_materials || []).length !== 1 ? 's' : ''} selected for customer quote &bull; {pricingData.totalSqm}m² &bull; {pricingData.vatRate === 0 ? 'VAT Zero Rated' : 'Prices inc. VAT'}
                </div>
                <p className="text-[10px] text-red-300/60 mt-1">Cost prices are for internal use only — never shown to customers</p>

                {/* Overall Quote Total Override */}
                {(() => {
                  const selectedMats = formData.selected_quote_materials || ['paulownia'];
                  const primaryId = selectedMats[0];
                  const primaryMat = pricingData.materials[primaryId];
                  if (!primaryMat) return null;
                  const shuttersTotal = primaryMat.retailTotal;
                  const grandTotal = shuttersTotal + blindsTotal;
                  return (
                    <div className="border-t border-white/20 mt-4 pt-3">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-bold">Total Quote</span>
                          <span className="text-xs text-white/40 ml-2">({primaryMat.name}{blindsTotal > 0 ? ' + Blinds' : ''})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/70 text-lg">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={grandTotal.toFixed(2)}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val) && val >= 0) {
                                const newShutters = val - blindsTotal;
                                if (newShutters >= 0) {
                                  setMaterialOverrides(prev => ({ ...prev, [primaryId]: newShutters.toFixed(2) }));
                                }
                              } else if (e.target.value === '') {
                                setMaterialOverrides(prev => { const n = {...prev}; delete n[primaryId]; return n; });
                              }
                            }}
                            className="w-32 bg-white/10 border-white/30 text-white text-xl font-bold text-right h-10 px-2"
                            data-testid="total-quote-override"
                          />
                        </div>
                      </div>
                      {blindsTotal > 0 && (
                        <div className="flex justify-between text-xs text-white/50 mt-1">
                          <span>Shutters: £{shuttersTotal.toFixed(2)} + Blinds: £{blindsTotal.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm text-white/60 mt-2">
                        <span>Deposit (60%)</span>
                        <span>£{(grandTotal * 0.6).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-white/60">
                        <span>Balance (40%)</span>
                        <span>£{(grandTotal * 0.4).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-[#8CB89F] mt-1">
                        <span>Pay Upfront (10% off)</span>
                        <span className="font-semibold">£{(grandTotal * 0.9).toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })()}

                {formData.include_blinds && formData.blinds.length > 0 && (
                  <div className="border-t border-white/20 mt-4 pt-3">
                    <h5 className="text-sm font-semibold text-white/80 mb-2">Blinds</h5>
                    <div className="space-y-1.5">
                      {formData.blinds.map((blind, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white/80">{blind.room_name || 'Blind'}</span>
                          <span className="text-white/90">£{parseFloat(blind.price || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Review Summary */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-3">Survey Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="font-medium">Customer:</span> {formData.customer_name}</div>
                <div><span className="font-medium">Email:</span> {formData.customer_email || 'Not provided'}</div>
                <div><span className="font-medium">Property:</span> {formData.property_type}</div>
                <div><span className="font-medium">Quote Materials:</span> {(formData.selected_quote_materials || []).map(id => MATERIALS.find(m => m.id === id)?.name).join(', ')}</div>
                <div><span className="font-medium">Postcode:</span> {formData.customer_postcode || 'N/A'}</div>
                <div><span className="font-medium">Openings:</span> {formData.openings.length}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 pt-4">
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleSubmit} 
                    disabled={loading || sendingQuote || !formData.customer_name}
                    variant="outline"
                    data-testid="submit-survey-btn"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Survey Only
                  </Button>
                  <Button 
                    onClick={handleSubmitAndSendQuote} 
                    disabled={loading || sendingQuote || !formData.customer_name || !formData.customer_email || formData.openings.length === 0}
                    className="bg-[#8CB89F] hover:bg-[#7aa88f]"
                    data-testid="submit-and-send-quote-btn"
                  >
                    {sendingQuote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Create Survey & Send Quote
                  </Button>
                </div>
              </div>
              {!formData.customer_email && (
                <p className="text-sm text-amber-600 text-right">
                  Add customer email to enable "Send Quote"
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSurveyDialog;
